<template>
  <div class="vcl-modal" role="dialog" aria-labelledby="dialog__label" aria-modal="true" @keyup.escape="handleEscape">
    <div ref="overlay" tabindex="0" class="vcl-modal__overlay" @click="close"></div>
    <div ref="modal-window" class="vcl-modal__window" :style="modalStyles">
      <div ref="modalCloseIcon" class="vcl-modal__close-icon" tabindex="0" role="button" :aria-label="closeDialogWindow" :title="closeDialogWindow" @keydown="handleCloseKeydown" @click="close"><vp-icon icon="close" /></div>
      <div id="dialog__label" class="vcl-modal__title" role="heading" aria-level="1">
        <slot name="header" />
      </div>
      <div ref="modalBody" class="vcl-modal__body">
        <slot />
      </div>
      <div ref="modalButtons" class="vcl-modal__buttons">
        <slot name="footer" :close="close" />
      </div>
    </div>
    <div ref="focusTrapEnd" tabindex="0"></div>
  </div>
</template>

<script>
import VpIcon from '../vp-icon/VpIcon.vue';

export default {
  name: 'VpModal',
  components: {
    VpIcon,
  },
  inject: ['close'],
  props: {
    id: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: null,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    }
  },
  data() {
    return {
      modalOverflow: null,
    };
  },
  computed: {
    closeDialogWindow() {
      return this.$i18n.t('closeDialogWindow');
    },
    modalStyles() {
      return {
        '--modal-width': this.width || this.styles.width || '680px',
        '--modal-max-width': this.styles.maxWidth || '95%',
        '--modal-max-height': this.styles.maxHeight || '95%',
        '--modal-header-height': this.styles.headerHeight || 'unset',
        '--modal-header-border': this.styles.headerBorder || 'unset',
        '--modal-header-padding': this.styles.headerPadding || '22px 32px 22px 48px',
        '--modal-overflow': this.modalOverflow || 'unset',
        '--modal-body-padding': this.styles.bodyPadding || '27px 48px 0',
        '--modal-buttons-padding': this.styles.buttonsPadding || '32px 0',
        '--modal-buttons-align': this.styles.buttonsAlign || 'center',
      };
    }
  },
  created() {
    window.addEventListener('resize', this.handleModalScroll);
  },
  watch: {
    $route() {
      this.close();
    },
  },
  mounted() {
    this.handleModalScroll();
    // We add a tabindex to the overlay to serve as a focus trap at the start of the modal.
    // If this beginning focus trap gains focus, we circle back to the last button in buttons div
    this.$refs.overlay.addEventListener('focusin', () => {
      let focusable = this.$refs.modalButtons.querySelectorAll('button, select, [href], input, [tabindex="0"]');
      if (focusable.length) {
        focusable[focusable.length - 1].focus();
      } else {
        focusable = this.$refs.modalBody.querySelectorAll('button, select, [href], input, [tabindex="0"]');
        if (focusable.length) {
          focusable[focusable.length - 1].focus();
        }
      }
    });

    // If the focus trap at the end of the modal gains focus, we circle back to the Close icon
    this.$refs.focusTrapEnd.addEventListener('focusin', () => {
      this.$refs.modalCloseIcon.focus();
    });

    // Focus on first focusable element within body and buttons, fallback to close icon
    let focusable = [];
    [this.$refs.modalBody, this.$refs.modalButtons].forEach((ref) => {
      focusable = focusable.concat(
        Array
          .from(ref.querySelectorAll('button, select, [href], input, [tabindex="0"]')) // Find elements
          .filter((el) => {
            if (el.offsetParent === null) { return false; } // remove hidden
            if (el.className === 'vcl-tooltip') { return false; } // remove tooltips
            return true;
          })
      );
    });
    if (focusable.length) {
      focusable[0].focus();
    } else {
      this.$refs.modalCloseIcon.focus();
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.handleModalScroll);
  },
  methods: {
    handleCloseKeydown(evt) {
      if ((evt.key === 'Enter') || (evt.key === 'Space') || (evt.key === ' ')) {
        this.close(evt);
      }
    },
    handleEscape(evt) {
      this.close(evt);
    },
    handleModalScroll() {
      const { overflowOffset = 50 } = this.styles;
      const modalWindow = this.$refs['modal-window'];
      const { top } = modalWindow.getBoundingClientRect();
      this.modalOverflow = top <= overflowOffset ? 'auto' : 'unset';
    },
  }
};
</script>

<style lang="scss" scoped>
.vcl-modal {
  $self: &;
  position: relative;
  &__overlay {
    background: rgba(0, 0, 0, 0.6);
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
  }
  &__window {
    background-color: #fff;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 0;
    position: fixed;
    z-index: 101;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: var(--modal-width);
    max-width: var(--modal-max-width);
    max-height: var(--modal-max-height);
    overflow: var(--modal-overflow);
    #{ $self }__close-icon {
      cursor: pointer;
      height: 16px;
      width: 16px;
      position: absolute;
      top: 29px;
      right: 29px;
      .icon {
        font-size: 16px;
        color: #AFAFAF;
      }
    }
    #{ $self }__title {
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
      box-sizing: border-box;
      color: #016699;
      font-size: 24px;
      text-align: left;
      user-select: none;
      height: var(--modal-header-height);
      padding: var(--modal-header-padding);
      border-bottom: var(--modal-header-border);
    }
    #{ $self }__body {
      padding: var(--modal-body-padding);
    }
    #{ $self }__buttons {
      display: flex;
      justify-content: center;
      align-items: var(--modal-buttons-align);
      padding: var(--modal-buttons-padding);
      margin: 0;
      &::v-deep button {
        margin: 0 8px;
      }
    }

    @media screen and (max-width: 700px) {
      width: 90%;
    }
     @media screen and (max-width: 600px) {
      width: 95%;
    }
  }
}

</style>
